import { createTheme } from '@mui/material/styles';

/**
 * The product theme for the authenticated app.
 *
 * The app previously ran on MUI's stock theme, so every button, link, tab and
 * focus ring was Material blue while the landing page a customer arrived from
 * was green — two different products by appearance. Defining the theme once
 * here rebrands every MUI surface at once; hand-written CSS reads the same
 * values from the custom properties in index.css.
 *
 * Values are kept in sync with those custom properties. They are repeated
 * rather than read from CSS because MUI needs real colours to compute hover,
 * disabled and contrast states.
 */

const INK = '#0f1f1a';
const GATE = '#006a4e';
const PRESENT = '#10b981';
const LATE = '#e8912d';
const RULE = '#dce5e1';
const MUTED = '#5b6b65';
const PAPER = '#fbfcfb';

// Noto Sans Bengali follows each Latin face: it draws only the Bangla glyphs those faces lack.
const DISPLAY = "'Bricolage Grotesque', 'Noto Sans Bengali', 'Trebuchet MS', system-ui, sans-serif";
const BODY = "'IBM Plex Sans', 'Noto Sans Bengali', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const heading = (fontSize: string, fontWeight = 700) => ({
    fontFamily: DISPLAY,
    fontWeight,
    fontSize,
    letterSpacing: '-0.02em',
    color: INK,
});

const appTheme = createTheme({
    palette: {
        primary: { main: GATE, dark: '#00543e', light: '#2f8a70', contrastText: '#ffffff' },
        // Jade is the "present" mark — it reads as the affirmative action colour.
        secondary: { main: PRESENT, dark: '#0ea271', light: '#4ecfa5', contrastText: '#04231a' },
        success: { main: PRESENT, contrastText: '#04231a' },
        // Amber means lateness everywhere in this product, so warnings inherit it.
        warning: { main: LATE, contrastText: '#2b1a06' },
        error: { main: '#c0392b' },
        info: { main: GATE },
        background: { default: PAPER, paper: '#ffffff' },
        text: { primary: INK, secondary: MUTED },
        divider: RULE,
    },

    shape: { borderRadius: 6 },

    typography: {
        fontFamily: BODY,
        h1: heading('2.2rem'),
        h2: heading('1.8rem'),
        h3: heading('1.5rem'),
        h4: heading('1.3rem'),
        h5: heading('1.15rem', 600),
        h6: heading('1.05rem', 600),
        button: { textTransform: 'none', fontWeight: 600 },
    },

    components: {
        // Sentence case, matching the landing page. MUI shouts by default.
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { textTransform: 'none', fontWeight: 600, borderRadius: 6 },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: { borderColor: RULE },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: { backgroundColor: '#f2f6f4' },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: { fontWeight: 600, color: INK },
                root: { borderColor: RULE },
            },
        },
        MuiChip: {
            styleOverrides: { root: { fontWeight: 600 } },
        },
        MuiAppBar: {
            styleOverrides: { root: { backgroundColor: '#ffffff', color: INK } },
        },
        MuiLink: {
            defaultProps: { underline: 'hover' },
            styleOverrides: { root: { color: GATE } },
        },
        MuiTab: {
            styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
        },
        // Amber focus ring, the same signal the landing page uses.
        MuiCssBaseline: {
            styleOverrides: {
                ':focus-visible': { outline: `2px solid ${LATE}`, outlineOffset: '2px' },
            },
        },
    },
});

export default appTheme;
